var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './Tips';
import '../../components/button';
import { t } from 'i18next';
import { BaseComponent } from '../../utils/BaseComponent';
import { trackEvent } from '../../utils/trackPulse';
import clsx from 'clsx';
import { consume } from '@lit-labs/context';
import { feedbackContext } from './FeedbackContext';
const iconContainer = `relative outline-none border-none w-full cursor-pointer
hover:scale-[115%] hover:drop-shadow-md
focus:scale-[115%] focus:drop-shadow-md
after:content-[""] after:absolute after:top-0 after:left-0 after:right-0 after:bottom-0 box-shadow-none`;
const iconAnimationClass = 'after:shadow-[0_0_0_1rem_rgba(0,0,0,0.3)] after:transition after:duration-300 after:linear after:opacity-0 after:rounded-full';
const activeClass = 'scale-[115%] drop-shadow-md';
const notActiveClass = 'opacity-40';
const translationX = (index) => {
    switch (index) {
        case 0:
            return `translate-x-[-0%]`;
        case 1:
            return `translate-x-[-100%]`;
        case 2:
            return `translate-x-[-200%]`;
        case 3:
            return `translate-x-[-300%]`;
        case 4:
            return `translate-x-[-400%]`;
        case 5:
            return `translate-x-[-500%]`;
        case 6:
            return `translate-x-[-600%]`;
        case 7:
            return `translate-x-[-700%]`;
        case 8:
            return `translate-x-[-800%]`;
        case 9:
            return `translate-x-[-900%]`;
        case 10:
            return `translate-x-[-1000%]`;
    }
};
let Questions = class Questions extends BaseComponent {
    constructor() {
        super(...arguments);
        // duration-500
        this._duration = 500;
        this._changing = false;
    }
    render() {
        var _a;
        const { active, answers, textReview, canSubmit, canSetNext, canSetPrev } = this.questionsAndStatus;
        const { questions, tradeRole } = (_a = this.questionsAndStatus) === null || _a === void 0 ? void 0 : _a.feedbackStatusAndQuestions;
        const getIconContainerClass = (index, answer) => {
            const active = this._chosenRating === index || (!this._chosenRating && (answer === null || answer === void 0 ? void 0 : answer.rating) === index);
            return clsx({
                [iconContainer]: true,
                [iconAnimationClass]: this._chosenRating === index,
                [activeClass]: active,
                [notActiveClass]: (!active) && (answer || this._chosenRating),
            });
        };
        const iconClass = 'block text-center position-relative cursor-pointer min-width-0 min-height-0 fill-primary hover:fill-primary--hover';
        const submitButton = html `
      <div class='flex justify-end'>
        <trust-button primary ?disabled='${!canSubmit()}' @click='${this.submit}' dataTest="submit">
          ${t('Complete')}
        </trust-button>
      </div>
    `;
        const IconButton = (value, ariaLabel, icon, index, answer) => {
            return html `
          <button
            class='${getIconContainerClass(value, answer)}'
            @click=${() => this.chooseRating(value)}
            tabindex=${active === index ? 0 : -1}
            aria-label=${ariaLabel}
            data-test='${`rate-${value}-at-question-${index}`}'
          >
            ${icon}
          </button>
      `;
        };
        const questionComponent = (index) => {
            const answer = answers.length > index ? answers[index] : undefined;
            return html `
        ${IconButton(1, t('Bad, absolutely I wouldn\'t shop with this person agian'), html `<trust-icon name="Bad" classNames=${iconClass}></trust-icon>`, index, answer)}
        ${IconButton(2, t('Meh, I probably wouldn\'t shop with this person again'), html `<trust-icon name="Meh" classNames=${iconClass}></trust-icon>`, index, answer)}
        ${IconButton(3, t('Ok, but could have been better'), html `<trust-icon name="Ok" classNames=${iconClass}></trust-icon>`, index, answer)}
        ${IconButton(4, t('Good, would love to shop with this person again'), html `<trust-icon name="Good" classNames=${iconClass}></trust-icon>`, index, answer)}
      `;
        };
        const smileys = questions.map((_, index) => {
            const hidden = active !== index;
            return html `
          <div class="shrink-0 w-full h-full" aria-hidden=${hidden}>
            <div class="justify-center gap-16 flex ${!hidden ? 'overflow-auto' : ''} sm:py-48 xs:py-32 p-16">
              ${questionComponent(index)}
            </div>
          </div>
       `;
        });
        const textReviewInView = active === questions.length;
        const textReviewHTML = html `
      <div class="shrink-0 w-full h-full px-16 ${!textReviewInView ? 'hidden' : ''}">
          <text-feedback-review
            transactionRole=${tradeRole}
            textReview='${textReview}'>
          </text-feedback-review>
          ${active === questions.length && !this.screen.isMobile ? submitButton : nothing}
      </div>
    `;
        return html `
      <div class="flex justify-center">
        <div class="max-w-lg relative w-full">
          ${!this.screen.isMobile ? html `
            <button class='disabled:invisible border-0 bg-transparent cursor-pointer absolute top-[5rem] z-10 -left-[3.5rem]' @click='${this.setPrev}' ?disabled='${!canSetPrev()}' data-test='previous' aria-label=${t('Back')}>
              <trust-icon name="ChevronLeft" classNames="w-32 h-32 fill-black hover:scale-125" ></trust-icon>
            </button>
          ` : nothing}
          <div class="overflow-hidden">
            <div class="flex flex-row flex-nowrap ${translationX(active)} transition-all duration-${this._duration} ease-in-out items-center">
              ${smileys}
              ${textReviewHTML}
            </div>
          </div>
          ${!this.screen.isMobile ? html `
            <button class='disabled:invisible border-0 bg-transparent cursor-pointer absolute top-[5rem] z-10 -right-[3.5rem]' @click='${this.setNext}' ?disabled='${!canSetNext()}' data-test='next' aria-label=${t('Next')}>
              <trust-icon name="ChevronRight" classNames="w-32 h-32 fill-black hover:scale-125"></trust-icon>
            </button>
          ` : nothing}
        </div>
      </div>
    `;
    }
    chooseRating(rating) {
        const { tradeRole } = this.questionsAndStatus.feedbackStatusAndQuestions;
        const { active } = this.questionsAndStatus;
        if (!this._chosenRating) {
            this._chosenRating = rating;
            setTimeout(() => {
                this._chosenRating = undefined;
                this.setRating(rating);
                trackEvent({
                    name: `Review ${tradeRole.toLowerCase()}`,
                    intent: 'Review',
                    type: 'Click'
                }, `Review step ${active + 1}`, 'Trust feedback', this.vertical, `Review step ${active + 2}`);
            }, this._duration / 2);
            this.dispatchEvent(new CustomEvent('focusHeader', {
                composed: true,
                bubbles: true
            }));
        }
    }
};
__decorate([
    consume({ context: feedbackContext, subscribe: true })
], Questions.prototype, "questionsAndStatus", void 0);
__decorate([
    property({ type: Object })
], Questions.prototype, "vertical", void 0);
__decorate([
    property({ attribute: false })
], Questions.prototype, "setPrev", void 0);
__decorate([
    property({ attribute: false })
], Questions.prototype, "setNext", void 0);
__decorate([
    property({ attribute: false })
], Questions.prototype, "submit", void 0);
__decorate([
    property({ attribute: false })
], Questions.prototype, "setRating", void 0);
__decorate([
    state()
], Questions.prototype, "_chosenRating", void 0);
__decorate([
    state()
], Questions.prototype, "_changing", void 0);
Questions = __decorate([
    customElement('trust-feedback-questions')
], Questions);
export { Questions };
//# sourceMappingURL=Questions.js.map